<template>
  <v-container fluid class="results-page">
    <!-- Hero Section -->
    <v-row justify="center" class="hero-section">
      <v-col cols="12" md="8" class="text-center">
        <div class="hero-content">
          <div class="trophy-icon-wrapper mb-6">
            <v-icon size="80" color="amber">mdi-trophy</v-icon>
          </div>
          <h1 class="results-heading font-weight-bold">
            🎉 <span class="gradient-title-text">Voting Results</span>
          </h1>
          <p class="results-subtitle">
            Congratulations to all our winners! Thank you for participating in our voting campaign.
          </p>
          <div class="divider-line mt-6 mb-0"></div>
        </div>
      </v-col>
    </v-row>

    <!-- Status Card -->
    <v-row v-if="loading" justify="center" class="mb-8">
      <v-col cols="12" md="6">
        <v-card class="status-card" outlined>
          <v-card-text class="text-center pa-6">
            <v-progress-circular
              indeterminate
              color="white"
              size="48"
              class="mb-4"
            ></v-progress-circular>
            <h3 class="status-title font-weight-bold">Loading Results...</h3>
            <p class="status-message">
              Please wait while we fetch the latest winners.
            </p>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-row v-else-if="error" justify="center" class="mb-8">
      <v-col cols="12" md="6">
        <v-card class="status-card" outlined>
          <v-card-text class="text-center pa-6">
            <v-icon size="48" color="error" class="mb-4">mdi-alert-circle</v-icon>
            <h3 class="status-title font-weight-bold">Error Loading Results</h3>
            <p class="status-message">
              {{ error }}
            </p>
            <v-btn
              color="primary"
              @click="fetchWinners"
              class="mt-4"
            >
              <v-icon left>mdi-refresh</v-icon>
              Try Again
            </v-btn>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-row v-else-if="!loading && winners.length === 0" justify="center" class="mb-8">
      <v-col cols="12" md="6">
        <v-card class="status-card" outlined>
          <v-card-text class="text-center pa-6">
            <v-icon size="48" color="info" class="mb-4">mdi-calendar-clock</v-icon>
            <h3 class="status-title font-weight-bold">Results Coming Soon!</h3>
            <p class="status-message">
              We're currently processing all the votes. Winners will be announced shortly. 
              Stay tuned for the exciting results!
            </p>
            <v-chip
              color="info"
              outlined
              class="mt-4"
            >
              <v-icon left>mdi-clock-outline</v-icon>
              Processing Votes
            </v-chip>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Winners Section -->
    <v-row v-if="!loading && !error && winners.length > 0" justify="center">
      <v-col cols="12" md="10">
        <v-card class="winners-card" outlined>
          <v-card-title class="winners-title text-center pa-6">
            <v-icon size="32" color="amber" class="mr-3">mdi-trophy</v-icon>
            Winners by Category
          </v-card-title>
          
          <v-card-text class="pa-6">
            <div v-for="(category, categoryIndex) in winners" :key="categoryIndex" class="category-section mb-8">
              <!-- Category Header -->
              <div class="category-header mb-4">
                <h3 class="category-title">
                  <v-icon size="24" color="primary" class="mr-2">mdi-star</v-icon>
                  {{ category.category }}
                </h3>
                <div class="category-divider"></div>
              </div>
              
              <!-- Subcategory Winners Grid -->
              <v-row>
                <v-col 
                  v-for="(subcategory, subIndex) in category.subcategories" 
                  :key="subIndex"
                  cols="12" sm="6" md="6" lg="3" xl="3"
                  class="mb-4"
                >
                  <v-card class="subcategory-winner-card" outlined>
                    <v-card-text class="card-content">
                      
                      <!-- Top Choice Badge -->
                      <div class="top-choice-badge">
                        <span class="badge-text">TOP CHOICE FOR {{ subcategory.name.toUpperCase() }} IN 2025</span>
                      </div>
                      
                      <!-- Product Image -->
                      <div class="product-image-container">
                        <img 
                          :src="subcategory.productImage || '/logo.png'" 
                          :alt="subcategory.productImage"
                          class="product-image"
                          @error="$event.target.src = '/logo.png'"
                        />

                      </div>
                      
                      <!-- Winner Product with Trophy -->
                      <div class="winner-section">
                        <div class="trophy-icon">
                          <v-icon size="24" color="amber">mdi-trophy</v-icon>
                        </div>
                        <div class="winner-product-name">{{ subcategory.winner }}</div>
                      </div>
                      
                      <!-- Vote Statistics -->
                      <div class="vote-stats">
                        <div class="vote-bar-container">
                          <div class="vote-bar">
                            <div 
                              class="vote-fill" 
                              :style="{ width: Math.min(subcategory.votePercentage, 100) + '%' }"
                            ></div>
                          </div>
                        </div>
                        <div class="vote-details">
                          <span class="vote-percentage">
                            {{ subcategory.votePercentage }}% of votes
                          </span>
                        </div>
                      </div>
                    </v-card-text>
                  </v-card>
                </v-col>
              </v-row>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import config from '@/config.js'

export default {
  name: 'ResultsPage',
  data() {
    return {
      winners: [],
      loading: true,
      error: null,
      statistics: {
        totalWinners: 0,
        totalCategories: 0
      }
    };
  },
  async mounted() {
    await this.fetchWinners();
  },
  methods: {
    async fetchWinners() {
      this.loading = true;
      this.error = null;
      
      try {
        const response = await fetch(`${config.API_BASE_URL}/winners`);
        if (!response.ok) {
          throw new Error('Failed to fetch winners');
        }
        
        const winnersData = await response.json();
        this.winners = this.transformWinnersData(winnersData);
        this.updateStatistics();
        
      } catch (error) {
        console.error('Error fetching winners:', error);
        this.error = 'Failed to load winners. Please try again later.';
      } finally {
        this.loading = false;
      }
    },
    
    transformWinnersData(winnersData) {
      // Group winners by category
      const categoryMap = new Map();
      
      winnersData.forEach(winner => {
        const categoryName = winner.categoryName || 'Unknown Category';
        
        if (!categoryMap.has(categoryName)) {
          categoryMap.set(categoryName, {
            category: categoryName,
            subcategories: []
          });
        }
        
        const subcategoryData = {
          name: winner.subcategoryName,
          winner: winner.productName,
          productImage: this.decodeHtmlEntities(winner.productImage),
          votes: winner.voteCount || 50,
          totalVotes: winner.totalVotes || 200,
          votePercentage: winner.votePercentage || 25
        };
        
        categoryMap.get(categoryName).subcategories.push(subcategoryData);
      });
      
      return Array.from(categoryMap.values());
    },
    
    updateStatistics() {
      this.statistics.totalWinners = this.winners.reduce((total, category) => 
        total + category.subcategories.length, 0
      );
      this.statistics.totalCategories = this.winners.length;
    },
    
    getTrophyColor(index) {
      const colors = ['amber', 'grey', 'brown', 'blue-grey', 'blue-grey'];
      return colors[index] || 'blue-grey';
    },
    getTrophyIcon(index) {
      const icons = ['mdi-trophy', 'mdi-medal', 'mdi-medal', 'mdi-star', 'mdi-star'];
      return icons[index] || 'mdi-star';
    },
    goToHome() {
      this.$router.push('/');
    },
    

    
    decodeHtmlEntities(text) {
      if (!text) return text;
      const textarea = document.createElement('textarea');
      textarea.innerHTML = text;
      return textarea.value;
    }
  }
};
</script>

<style scoped>
.results-page {
  min-height: 100vh;
  padding: 10px 0;
  background: transparent;
}

.hero-section {
  margin-bottom: 0px;
}

.hero-content {
  padding: 60px 20px 0px 20px;
}

.trophy-icon-wrapper {
  display: inline-block;
  padding: 24px;
  background: #FFFFFF;
  border-radius: 50%;
  border: 2.5px solid #00D4FF;
  box-shadow: 0 4px 15px rgba(0, 212, 255, 0.15);
  animation: pulse-glow 3s ease-in-out infinite;
}

@keyframes pulse-glow {
  0%, 100% { 
    box-shadow: 0 4px 15px rgba(0, 212, 255, 0.15);
  }
  50% { 
    box-shadow: 0 4px 15px rgba(0, 212, 255, 0.15), 0 0 0 10px rgba(0, 212, 255, 0.08);
  }
}

.results-heading {
  font-size: clamp(2.2rem, 5vw, 4rem);
  margin-bottom: 24px;
  font-weight: 800;
  text-shadow: 0 4px 15px rgba(0, 0, 0, 0.6);
  letter-spacing: -0.02em;
  line-height: 1.1;
  color: #FFFFFF;
}

.gradient-title-text {
  color: #FFFFFF;
}

.results-subtitle {
  font-size: 1.3rem;
  color: #FFFFFF;
  line-height: 1.7;
  max-width: 700px;
  margin: 0 auto;
  font-weight: 700;
  letter-spacing: 0.01em;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.5);
}

.divider-line {
  height: 3px;
  background: linear-gradient(90deg, transparent, rgba(0, 212, 255, 0.4), transparent);
  border-radius: 2px;
  margin: 0 auto;
  width: 80px;
  box-shadow: 0 2px 8px rgba(0, 212, 255, 0.15);
}

.status-card {
  background: rgba(44, 44, 52, 0.85) !important;
  backdrop-filter: blur(15px);
  -webkit-backdrop-filter: blur(15px);
  border: 1.5px solid rgba(0, 212, 255, 0.3) !important;
  border-radius: 24px;
  box-shadow: 0 15px 35px rgba(0, 212, 255, 0.08) !important;
  transition: all 0.3s ease;
}

.status-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 20px 45px rgba(0, 212, 255, 0.15) !important;
}

.status-title {
  color: #00D4FF;
  font-size: 1.8rem;
  margin-bottom: 12px;
  font-weight: 800;
}

.status-message {
  color: #B0BEC5;
  font-size: 1.1rem;
  line-height: 1.6;
}

.winners-card {
  background: rgba(44, 44, 52, 0.85) !important;
  backdrop-filter: blur(15px);
  -webkit-backdrop-filter: blur(15px);
  border: 1.5px solid rgba(0, 212, 255, 0.3) !important;
  border-radius: 24px;
  box-shadow: 0 15px 35px rgba(0, 212, 255, 0.08) !important;
  overflow: hidden;
  transition: all 0.3s ease;
}

.winners-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 20px 45px rgba(0, 212, 255, 0.15) !important;
}

.winners-title {
  background: linear-gradient(135deg, #2C2C34 0%, #1A1A20 100%) !important;
  color: #00D4FF !important;
  font-size: 1.8rem;
  border-bottom: 1.5px solid rgba(0, 212, 255, 0.3) !important;
  font-weight: 800;
}

.winners-list {
  padding: 0;
}

.winner-item {
  padding: 20px 24px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  transition: all 0.3s ease;
}

.winner-item:hover {
  background: rgba(0, 212, 255, 0.05) !important;
  transform: translateX(5px);
}

.winner-item:last-child {
  border-bottom: none;
}

.winner-item.first-place {
  background: linear-gradient(135deg, rgba(255, 193, 7, 0.12), rgba(255, 193, 7, 0.04)) !important;
  border-left: 4px solid #ffc107 !important;
}

.winner-item.second-place {
  background: linear-gradient(135deg, rgba(158, 158, 158, 0.12), rgba(158, 158, 158, 0.04)) !important;
  border-left: 4px solid #9e9e9e !important;
}

.winner-item.third-place {
  background: linear-gradient(135deg, rgba(141, 110, 99, 0.12), rgba(141, 110, 99, 0.04)) !important;
  border-left: 4px solid #8d6e63 !important;
}

.winner-avatar {
  margin-right: 16px;
}

.place-badge {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 1.2rem;
  color: white;
  background: linear-gradient(135deg, #2C2C34, #00D4FF) !important;
  box-shadow: 0 4px 12px rgba(0, 212, 255, 0.3) !important;
}

.winner-name {
  font-size: 1.2rem;
  color: #FFFFFF;
  margin-bottom: 4px;
  font-weight: 700;
}

.winner-subcategory {
  color: #B0BEC5;
  font-size: 0.9rem;
  margin-bottom: 2px;
}

.winner-votes {
  color: #00D4FF;
  font-weight: 700;
  font-size: 0.9rem;
}

/* New Category and Subcategory Styles */
.category-section {
  margin-bottom: 40px;
}

.category-header {
  margin-bottom: 24px;
}

.category-title {
  font-size: 1.5rem;
  font-weight: 800;
  color: #FFFFFF;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
}

.category-divider {
  height: 2px;
  background: linear-gradient(90deg, #2C2C34, #00D4FF);
  border-radius: 1px;
  width: 100%;
}

.subcategory-winner-card {
  background: rgba(44, 44, 52, 0.85) !important;
  backdrop-filter: blur(15px);
  -webkit-backdrop-filter: blur(15px);
  border: 1.5px solid rgba(0, 212, 255, 0.2) !important;
  border-radius: 20px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15) !important;
  position: relative;
  overflow: hidden;
  height: 420px;
  display: flex;
  flex-direction: column;
  transition: all 0.3s ease;
}

.card-content {
  padding: 24px !important;
  display: flex !important;
  flex-direction: column !important;
  height: 100% !important;
  box-sizing: border-box !important;
  position: relative;
}

.subcategory-winner-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(90deg, #2C2C34, #00D4FF);
  opacity: 0;
  transition: opacity 0.3s ease;
}

.subcategory-winner-card:hover {
  transform: translateY(-6px);
  box-shadow: 0 16px 48px rgba(0, 212, 255, 0.18) !important;
  border-color: rgba(0, 212, 255, 0.5) !important;
}

.subcategory-winner-card:hover::before {
  opacity: 1;
}

/* Bookmark Icon */
.bookmark-icon {
  position: absolute;
  top: 16px;
  right: 16px;
  z-index: 2;
}

/* Top Choice Badge */
.top-choice-badge {
  margin-bottom: 20px;
  padding: 8px 12px;
  background: linear-gradient(135deg, #2C2C34, #00D4FF);
  border-radius: 12px;
  text-align: center;
  box-shadow: 0 4px 12px rgba(0, 212, 255, 0.2);
}

.badge-text {
  color: white;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  line-height: 1.2;
}

/* Product Image */
.product-image-container {
  display: flex;
  justify-content: center;
  margin-bottom: 20px;
  padding: 0 20px;
}

.product-image {
  width: 120px;
  height: 120px;
  object-fit: contain;
  border-radius: 16px;
  background: white;
  padding: 12px;
}

/* Winner Section */
.winner-section {
  display: flex;
  align-items: center;
  margin-bottom: 16px;
  padding: 16px;
  background: linear-gradient(135deg, rgba(255, 193, 7, 0.12), rgba(255, 193, 7, 0.04)) !important;
  border-radius: 16px;
  border: 1px solid rgba(255, 193, 7, 0.2) !important;
}

.trophy-icon {
  margin-right: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: linear-gradient(135deg, #ffc107, #ff8f00);
  border-radius: 50%;
  box-shadow: 0 4px 12px rgba(255, 193, 7, 0.3);
}

.winner-product-name {
  color: #FFFFFF;
  font-size: 1.1rem;
  font-weight: 700;
  line-height: 1.3;
  word-wrap: break-word;
  overflow-wrap: break-word;
  hyphens: auto;
  flex: 1;
}

.vote-stats {
  margin-top: 20px;
  padding-top: 12px;
  display: block;
  visibility: visible;
  opacity: 1;
}

.vote-bar-container {
  width: 100%;
  margin-bottom: 12px;
}

.vote-bar {
  width: 100%;
  height: 10px;
  background: rgba(255, 255, 255, 0.08);
  border-radius: 8px;
  overflow: hidden;
  box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.1);
}

.vote-fill {
  height: 100%;
  background: linear-gradient(90deg, #2C2C34 0%, #00D4FF 100%);
  border-radius: 8px;
  transition: width 0.8s ease;
  box-shadow: 0 2px 8px rgba(0, 212, 255, 0.3);
  position: relative;
}

.vote-fill::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent);
  border-radius: 8px;
}

.vote-details {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 4px;
}

.vote-count {
  color: #00D4FF;
  font-weight: 700;
  font-size: 1rem;
}

.vote-percentage {
  color: #00D4FF;
  font-size: 1rem;
  font-weight: 700;
  text-align: center;
}

.stats-card {
  background: rgba(44, 44, 52, 0.85) !important;
  backdrop-filter: blur(15px);
  -webkit-backdrop-filter: blur(15px);
  border: 1.5px solid rgba(0, 212, 255, 0.3) !important;
  border-radius: 24px;
  box-shadow: 0 15px 35px rgba(0, 212, 255, 0.08) !important;
  transition: all 0.3s ease;
}

.stats-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 20px 45px rgba(0, 212, 255, 0.15) !important;
}

.stats-title {
  background: linear-gradient(135deg, #2C2C34, #1A1A20) !important;
  color: #00D4FF !important;
  font-size: 1.8rem;
  border-bottom: 1.5px solid rgba(0, 212, 255, 0.3) !important;
  font-weight: 800;
}

.stat-item {
  padding: 20px;
  transition: transform 0.3s ease;
}

.stat-item:hover {
  transform: translateY(-5px);
}

.stat-icon-wrapper {
  margin-bottom: 12px;
}

.stat-number {
  font-size: 2.5rem;
  font-weight: bold;
  color: #00D4FF;
  margin-bottom: 8px;
  text-shadow: 0 0 10px rgba(0, 212, 255, 0.2);
}

.stat-label {
  color: #B0BEC5;
  font-size: 1rem;
  font-weight: 500;
}

.cta-btn {
  background: linear-gradient(135deg, #2C2C34 0%, #00D4FF 50%, #2C2C34 100%) !important;
  background-size: 200% auto !important;
  border: 1.5px solid rgba(0, 212, 255, 0.4) !important;
  border-radius: 16px;
  box-shadow: 0 8px 24px rgba(0, 212, 255, 0.25) !important;
  transition: all 0.3s ease !important;
  text-transform: uppercase;
  letter-spacing: 1px;
  font-weight: 600;
  padding: 16px 32px;
  font-size: 1rem;
  position: relative;
  overflow: hidden;
  color: white !important;
}

.cta-btn::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
  transition: left 0.5s ease;
}

.cta-btn:hover {
  background-position: right center !important;
  transform: translateY(-3px) scale(1.02);
  box-shadow: 0 12px 32px rgba(0, 212, 255, 0.4) !important;
}

.cta-btn:hover::before {
  left: 100%;
}

@media (max-width: 959px) {
  .results-page {
    padding: 20px 0;
  }
  
  .hero-content {
    padding: 40px 16px 0px 16px;
  }
  
  .trophy-icon-wrapper {
    padding: 30px;
  }
  
  .results-heading {
    font-size: 2.8rem;
    margin-bottom: 20px;
    line-height: 1.2;
  }
  
  .results-subtitle {
    font-size: 1.2rem;
    line-height: 1.5;
    padding: 0 10px;
  }
  
  .divider-line {
    width: 60px;
    height: 2px;
  }
  
  .status-card {
    margin: 0 16px;
  }
  
  .winners-card {
    margin: 0 16px;
  }
  
  .stats-card {
    margin: 0 16px;
  }
  
  .subcategory-winner-card {
    margin-bottom: 20px;
    height: auto;
    min-height: 320px;
  }
  
  .subcategory-winner-card .v-card__text {
    padding: 20px !important;
  }
  
  .top-choice-badge {
    margin-bottom: 16px;
    padding: 6px 10px;
  }
  
  .badge-text {
    font-size: 0.7rem;
  }
  
  .winner-section {
    margin-bottom: 20px;
    padding: 12px;
  }
  
  .trophy-icon {
    width: 36px;
    height: 36px;
    margin-right: 10px;
  }
  
  .winner-product-name {
    font-size: 1rem;
  }
  
  .vote-bar {
    height: 8px;
  }
  
  .vote-count {
    font-size: 0.95rem;
  }
  
  .vote-percentage {
    font-size: 0.85rem;
  }
  
  .category-title {
    font-size: 1.4rem;
    margin-bottom: 16px;
  }
  
  .stat-number {
    font-size: 2.2rem;
  }
  
  .stat-label {
    font-size: 0.9rem;
  }
}

/* Desktop styles - ensure vote stats are visible */
@media (min-width: 961px) {
  .vote-stats {
    display: block !important;
    visibility: visible !important;
    opacity: 1 !important;
    margin-top: 20px !important;
    position: relative !important;
    z-index: 1 !important;
  }
  
  .vote-bar-container {
    display: block !important;
    visibility: visible !important;
  }
  
  .vote-details {
    display: flex !important;
    visibility: visible !important;
  }
  
  /* Constrain card content width on larger screens */
  .subcategory-winner-card {
    max-width: 400px;
    margin: 0 auto;
  }
  
  .card-content {
    max-width: 100%;
  }
  
  .product-image-container {
    max-width: 100%;
  }
  
  .product-image {
    max-width: 100%;
    height: auto;
  }
}

/* Large desktop screens - show 4 cards per row */
@media (min-width: 1200px) {
  .subcategory-winner-card {
    max-width: 380px;
  }
  
  .product-image {
    width: 100px;
    height: 100px;
  }
  
  .top-choice-badge {
    margin-bottom: 16px;
    padding: 6px 10px;
  }
  
  .badge-text {
    font-size: 0.7rem;
  }
  
  .winner-section {
    margin-bottom: 14px;
    padding: 12px;
  }
  
  .trophy-icon {
    width: 36px;
    height: 36px;
  }
  
  .winner-product-name {
    font-size: 1rem;
  }
}

@media (max-width: 600px) {
  .results-heading {
    font-size: 2.2rem;
    margin-bottom: 16px;
  }
  
  .results-subtitle {
    font-size: 1.1rem;
    padding: 0 5px;
  }
  
  .trophy-icon-wrapper {
    padding: 25px;
  }
  
  .hero-content {
    padding: 30px 12px 0px 12px;
  }
  
  .subcategory-winner-card {
    min-height: 400px !important;
    height: auto !important;
    margin-bottom: 16px;
  }
  
  .card-content {
    padding: 16px !important;
  }
  
  .top-choice-badge {
    margin-bottom: 14px !important;
    padding: 6px 8px !important;
  }
  
  .badge-text {
    font-size: 0.65rem !important;
    line-height: 1.2 !important;
  }
  
  .winner-section {
    margin-bottom: 18px !important;
    padding: 10px !important;
  }
  
  .trophy-icon {
    width: 32px !important;
    height: 32px !important;
    margin-right: 8px !important;
  }
  
  .winner-product-name {
    font-size: 0.95rem !important;
    line-height: 1.3 !important;
    word-wrap: break-word !important;
    overflow-wrap: break-word !important;
    hyphens: auto !important;
  }
  
  .vote-bar {
    height: 7px;
  }
  
  .vote-count {
    font-size: 0.9rem;
  }
  
  .vote-percentage {
    font-size: 0.8rem;
  }
  
  .category-title {
    font-size: 1.2rem;
  }
  
  .winners-title {
    font-size: 1.3rem !important;
    padding: 16px 12px !important;
    line-height: 1.3 !important;
    word-wrap: break-word !important;
    overflow-wrap: break-word !important;
  }
  
  .stats-title {
    font-size: 1.3rem !important;
    padding: 16px 12px !important;
    line-height: 1.3 !important;
    word-wrap: break-word !important;
    overflow-wrap: break-word !important;
  }
}

@media (max-width: 480px) {
  .subcategory-winner-card {
    min-height: 360px !important;
    height: auto !important;
    margin-bottom: 12px;
  }
  
  .card-content {
    padding: 12px !important;
  }
  
  .top-choice-badge {
    margin-bottom: 12px !important;
    padding: 5px 6px !important;
  }
  
  .badge-text {
    font-size: 0.65rem !important;
    line-height: 1.1 !important;
  }
  
  .winner-section {
    margin-bottom: 16px !important;
    padding: 8px !important;
  }
  
  .trophy-icon {
    width: 28px !important;
    height: 28px !important;
    margin-right: 6px !important;
  }
  
  .winner-product-name {
    font-size: 0.85rem !important;
    line-height: 1.2 !important;
    word-wrap: break-word !important;
    overflow-wrap: break-word !important;
    hyphens: auto !important;
  }
  
  .winners-title {
    font-size: 1.1rem !important;
    padding: 12px 8px !important;
    line-height: 1.2 !important;
  }
  
  .stats-title {
    font-size: 1.1rem !important;
    padding: 12px 8px !important;
    line-height: 1.2 !important;
  }
}
</style>
